/**
 * ProductsPage - Gestione unificata Prodotti e Cataloghi
 * Layout: Sidebar cataloghi + Area principale prodotti
 */
import React, { useEffect, useState, useCallback } from 'react';
import { getErrorMessage, formatCurrency } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../contexts/I18nContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import { Skeleton } from '../components/ui/skeleton';
import {
  Package,
  Plus,
  Pencil,
  Trash2,
  Search,
  Upload,
  Sparkles,
  Loader2,
  FolderOpen,
  FolderPlus,
  MoreVertical,
  Eye,
  EyeOff,
  Star,
  Check,
  X,
  Image,
  ImagePlus,
  Grid3X3,
  List,
  Filter,
  ChevronRight,
  Boxes,
  Tags,
  HelpCircle,
  FileSpreadsheet,
  ArrowRight,
  BookOpen,
  Lightbulb,
  MousePointerClick
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { cn } from '@/lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

interface Product {
  id: string;
  name: string;
  description?: string;
  price: number;
  unit: string;
  category?: string;
  sku?: string;
  image_url?: string;
  is_active: boolean;
  stock_quantity?: number;
  min_order_quantity?: number;
  catalogs?: string[];
}

interface Catalog {
  id: string;
  name: string;
  description?: string;
  is_default: boolean;
  is_active: boolean;
  product_count: number;
  product_ids?: string[];
}

const ProductsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  
  // State
  const [products, setProducts] = useState<Product[]>([]);
  const [catalogs, setCatalogs] = useState<Catalog[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCatalog, setSelectedCatalog] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  
  // Dialogs
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [catalogDialogOpen, setCatalogDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [addToCatalogDialogOpen, setAddToCatalogDialogOpen] = useState(false);
  
  // Edit state
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [editingCatalog, setEditingCatalog] = useState<Catalog | null>(null);
  const [deletingItem, setDeletingItem] = useState<{ type: 'product' | 'catalog', id: string, name: string } | null>(null);
  
  // Form state
  const [productForm, setProductForm] = useState({
    name: '',
    description: '',
    price: '',
    unit: 'pz',
    category: '',
    sku: '',
    is_active: true,
    min_order_quantity: '1'
  });
  const [catalogForm, setCatalogForm] = useState({
    name: '',
    description: '',
    is_default: false
  });
  const [saving, setSaving] = useState(false);
  
  // Tutorial e onboarding
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);
  const [showIntroBanner, setShowIntroBanner] = useState(() => {
    return localStorage.getItem('catalog_intro_dismissed') !== 'true';
  });
  
  const dismissIntroBanner = () => {
    setShowIntroBanner(false);
    localStorage.setItem('catalog_intro_dismissed', 'true');
  };

  // Fetch data
  const fetchProducts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/products?item_type=product`);
      setProducts(res.data.products || res.data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Errore nel caricamento dei prodotti');
    }
  }, []);

  const fetchCatalogs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/catalogs`);
      setCatalogs(res.data.catalogs || res.data || []);
    } catch (error) {
      console.error('Error fetching catalogs:', error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchProducts(), fetchCatalogs()]);
      setLoading(false);
    };
    loadData();
  }, [fetchProducts, fetchCatalogs]);

  // Filter products
  const filteredProducts = products.filter(product => {
    const matchesSearch = !searchQuery || 
      product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.sku?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.category?.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Filtra per catalogo usando product_ids dal catalogo selezionato
    let matchesCatalog = true;
    if (selectedCatalog) {
      const catalog = catalogs.find(c => c.id === selectedCatalog);
      matchesCatalog = catalog?.product_ids?.includes(product.id) ?? false;
    }
    
    return matchesSearch && matchesCatalog;
  });

  // Handlers
  const handleOpenProductDialog = (product?: Product) => {
    if (product) {
      setEditingProduct(product);
      setProductForm({
        name: product.name,
        description: product.description || '',
        price: (product.price ?? '').toString(),
        unit: product.unit || 'pz',
        category: product.category || '',
        sku: product.sku || '',
        is_active: product.is_active ?? true,
        min_order_quantity: (product.min_order_quantity ?? 1).toString()
      });
    } else {
      setEditingProduct(null);
      setProductForm({
        name: '',
        description: '',
        price: '',
        unit: 'pz',
        category: '',
        sku: '',
        is_active: true,
        min_order_quantity: '1'
      });
    }
    setProductDialogOpen(true);
  };

  const handleOpenCatalogDialog = (catalog?: Catalog) => {
    if (catalog) {
      setEditingCatalog(catalog);
      setCatalogForm({
        name: catalog.name,
        description: catalog.description || '',
        is_default: catalog.is_default
      });
    } else {
      setEditingCatalog(null);
      setCatalogForm({
        name: '',
        description: '',
        is_default: false
      });
    }
    setCatalogDialogOpen(true);
  };

  const handleSaveProduct = async () => {
    if (!productForm.name || !productForm.price) {
      toast.error('Nome e prezzo sono obbligatori');
      return;
    }
    
    setSaving(true);
    try {
      const data = {
        ...productForm,
        price: parseFloat(productForm.price),
        min_order_quantity: parseInt(productForm.min_order_quantity) || 1
      };
      
      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, data);
        toast.success('Prodotto aggiornato');
      } else {
        await axios.post(`${API}/products`, data);
        toast.success('Prodotto creato');
      }
      
      setProductDialogOpen(false);
      fetchProducts();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore nel salvataggio'));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCatalog = async () => {
    if (!catalogForm.name) {
      toast.error('Il nome è obbligatorio');
      return;
    }
    
    setSaving(true);
    try {
      if (editingCatalog) {
        await axios.put(`${API}/catalogs/${editingCatalog.id}`, catalogForm);
        toast.success('Catalogo aggiornato');
      } else {
        await axios.post(`${API}/catalogs`, catalogForm);
        toast.success('Catalogo creato');
      }
      
      setCatalogDialogOpen(false);
      fetchCatalogs();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore nel salvataggio'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingItem) return;
    
    try {
      if (deletingItem.type === 'product') {
        await axios.delete(`${API}/products/${deletingItem.id}`);
        toast.success('Prodotto eliminato');
        fetchProducts();
      } else {
        await axios.delete(`${API}/catalogs/${deletingItem.id}`);
        toast.success('Catalogo eliminato');
        if (selectedCatalog === deletingItem.id) {
          setSelectedCatalog(null);
        }
        fetchCatalogs();
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore nell\'eliminazione'));
    } finally {
      setDeleteDialogOpen(false);
      setDeletingItem(null);
    }
  };

  const handleAddToCatalog = async (catalogId: string) => {
    if (selectedProducts.length === 0) return;
    
    try {
      await axios.post(`${API}/catalogs/${catalogId}/products`, {
        product_ids: selectedProducts
      });
      toast.success(`${selectedProducts.length} prodotti aggiunti al catalogo`);
      setSelectedProducts([]);
      setAddToCatalogDialogOpen(false);
      fetchProducts();
      fetchCatalogs();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore nell\'aggiunta'));
    }
  };

  const handleRemoveFromCatalog = async (productId: string, catalogId: string) => {
    try {
      await axios.delete(`${API}/catalogs/${catalogId}/products/${productId}`);
      toast.success('Prodotto rimosso dal catalogo');
      fetchProducts();
      fetchCatalogs();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore nella rimozione'));
    }
  };

  const toggleProductSelection = (productId: string) => {
    setSelectedProducts(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const selectAllProducts = () => {
    if (selectedProducts.length === filteredProducts.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(filteredProducts.map(p => p.id));
    }
  };

  const totalProducts = products.length;
  const catalogProductCount = selectedCatalog 
    ? (catalogs.find(c => c.id === selectedCatalog)?.product_ids?.length || 0)
    : totalProducts;



  // Sub-cataloghi filtrati (escludi default "Tutti i Prodotti")
  const visibleCatalogs = catalogs.filter(c => !(c.is_default && c.name.toLowerCase().includes('tutti')));

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="flex gap-3"><Skeleton className="h-9 w-32" /><Skeleton className="h-9 w-32" /></div>
        <div className="space-y-2">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-14 w-full" />)}</div>
      </div>
    );
  }

  // ========== EMPTY STATE ==========
  if (products.length === 0) {
    return (
      <div className="p-6 space-y-8 max-w-4xl mx-auto" data-testid="products-page">
        <div className="text-center pt-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-primary/10 mb-6">
            <Package className="h-10 w-10 text-primary" />
          </div>
          <h1 className="text-3xl font-heading font-bold mb-3">Il tuo Catalogo Prodotti</h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Qui gestisci tutti i prodotti che offri ai tuoi clienti. Il catalogo è il cuore della piattaforma: 
            i tuoi clienti lo consulteranno per fare ordini, <strong>senza bisogno di chiamate o WhatsApp</strong>.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="group cursor-pointer hover:shadow-lg hover:border-primary/50 transition-all" onClick={() => handleOpenProductDialog()}>
            <CardContent className="p-6 text-center space-y-3">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-blue-50 dark:bg-blue-950/30 group-hover:bg-blue-100 transition-colors"><Plus className="h-7 w-7 text-blue-600" /></div>
              <h3 className="font-semibold">Aggiungi Manualmente</h3>
              <p className="text-sm text-muted-foreground">Inserisci un prodotto alla volta con nome, prezzo e unità di misura</p>
              <p className="text-xs text-primary font-medium">Ideale per pochi prodotti</p>
            </CardContent>
          </Card>
          <Card className="group cursor-pointer hover:shadow-lg hover:border-primary/50 transition-all" onClick={() => navigate('/catalog/import')}>
            <CardContent className="p-6 text-center space-y-3">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-green-50 dark:bg-green-950/30 group-hover:bg-green-100 transition-colors"><FileSpreadsheet className="h-7 w-7 text-green-600" /></div>
              <h3 className="font-semibold">Importa da Excel</h3>
              <p className="text-sm text-muted-foreground">Hai già un listino in Excel o CSV? Caricalo e importa tutto in un click</p>
              <p className="text-xs text-green-600 font-medium">Il più veloce per iniziare</p>
            </CardContent>
          </Card>
          <Card className="group cursor-pointer hover:shadow-lg hover:border-primary/50 transition-all" onClick={() => navigate('/catalog/import-ai')}>
            <CardContent className="p-6 text-center space-y-3">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-purple-50 dark:bg-purple-950/30 group-hover:bg-purple-100 transition-colors"><Sparkles className="h-7 w-7 text-purple-600" /></div>
              <h3 className="font-semibold">Importa con AI</h3>
              <p className="text-sm text-muted-foreground">Incolla un testo o carica un PDF: l'intelligenza artificiale fa il resto</p>
              <p className="text-xs text-purple-600 font-medium">Per listini non strutturati</p>
            </CardContent>
          </Card>
        </div>
        <div className="text-center">
          <Button variant="ghost" className="text-muted-foreground gap-2" onClick={() => { setTutorialStep(0); setShowTutorial(true); }}>
            <HelpCircle className="h-4 w-4" /> Come funziona? Guarda il tutorial
          </Button>
        </div>
      </div>
    );
  }

  // ========== NORMAL STATE ==========
  return (
    <div className="flex flex-col h-[calc(100vh-4rem)]" data-testid="products-page">
      <div className="border-b bg-gradient-to-br from-primary/5 via-background to-emerald-50/50 dark:from-primary/10 dark:via-background dark:to-emerald-950/10">
          <div className="px-6 py-5 max-w-5xl">
            <div className="flex items-start gap-4">
              <div className="bg-primary/10 rounded-xl p-3 shrink-0">
                <BookOpen className="h-7 w-7 text-primary" />
              </div>
              <div className="flex-1 space-y-2">
                <h2 className="text-lg font-semibold">Benvenuto nel tuo Catalogo Prodotti</h2>
                <p className="text-sm text-muted-foreground leading-relaxed max-w-3xl">
                  Questa è la vetrina digitale dei tuoi prodotti. I tuoi clienti consulteranno questo elenco 
                  per fare ordini direttamente dalla piattaforma — <strong>niente più telefonate, 
                  WhatsApp o fogli volanti</strong>. Puoi aggiungere prodotti uno alla volta, oppure 
                  importare tutto da un file Excel in pochi secondi.
                </p>
                <div className="flex items-center gap-3 pt-1">
                  <Button 
                    size="sm" 
                    onClick={() => { setTutorialStep(0); setShowTutorial(true); }}
                    className="gap-2"
                  >
                    <MousePointerClick className="h-4 w-4" />
                    Scopri come funziona
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

      <div className="px-6 pt-5 pb-4 border-b bg-background space-y-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-heading font-bold">
              {selectedCatalog ? catalogs.find(c => c.id === selectedCatalog)?.name || 'Catalogo' : 'Catalogo Prodotti'}
            </h1>
            <p className="text-sm text-muted-foreground">{catalogProductCount} prodotti{selectedCatalog ? ' in questo sotto-catalogo' : ' totali'}</p>
          </div>
          <div className="flex items-center gap-2">
            {selectedProducts.length > 0 && (
              <Button variant="outline" onClick={() => setAddToCatalogDialogOpen(true)}>
                <FolderPlus className="h-4 w-4 mr-2" /> Sposta in sotto-catalogo ({selectedProducts.length})
              </Button>
            )}
            <Button variant="outline" onClick={() => navigate('/catalog/import')}><Upload className="h-4 w-4 mr-2" /> Importa Excel</Button>
            <Button variant="outline" onClick={() => navigate('/catalog/import-ai')} className="border-primary/50 hover:bg-primary/10"><Sparkles className="h-4 w-4 mr-2 text-primary" /> Importa con AI</Button>
            <Button onClick={() => handleOpenProductDialog()}><Plus className="h-4 w-4 mr-2" /> Nuovo Prodotto</Button>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Cerca prodotti..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9" />
          </div>
          <div className="flex items-center gap-1.5 flex-1 overflow-x-auto">
            <button onClick={() => setSelectedCatalog(null)} className={cn("px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors", !selectedCatalog ? "bg-primary text-primary-foreground" : "bg-muted hover:bg-muted/80 text-muted-foreground")}>
              Tutti ({totalProducts})
            </button>
            {visibleCatalogs.map(catalog => (
              <button key={catalog.id} onClick={() => setSelectedCatalog(catalog.id)} className={cn("px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors", selectedCatalog === catalog.id ? "bg-primary text-primary-foreground" : "bg-muted hover:bg-muted/80 text-muted-foreground")}>
                {catalog.name} ({catalog.product_ids?.length || 0})
              </button>
            ))}
            <button onClick={() => handleOpenCatalogDialog()} className="px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap text-muted-foreground border border-dashed border-muted-foreground/30 hover:border-primary hover:text-primary transition-colors flex items-center gap-1">
              <Plus className="h-3.5 w-3.5" /> Sotto-catalogo
            </button>
          </div>
          <div className="flex items-center border rounded-lg shrink-0">
            <Button variant={viewMode === 'grid' ? 'secondary' : 'ghost'} size="icon" className="rounded-r-none" onClick={() => setViewMode('grid')}><Grid3X3 className="h-4 w-4" /></Button>
            <Button variant={viewMode === 'list' ? 'secondary' : 'ghost'} size="icon" className="rounded-l-none" onClick={() => setViewMode('list')}><List className="h-4 w-4" /></Button>
          </div>
        </div>
      </div>

      <ScrollArea className="flex-1">
        {filteredProducts.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
            <Package className="h-12 w-12 mb-4" />
            <p className="font-medium">Nessun prodotto trovato</p>
            <p className="text-sm">{searchQuery ? 'Prova con una ricerca diversa' : selectedCatalog ? 'Questo sotto-catalogo è vuoto' : 'Aggiungi il tuo primo prodotto'}</p>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
            {filteredProducts.map(product => (
              <Card key={product.id} className={cn("group cursor-pointer transition-all hover:shadow-md", selectedProducts.includes(product.id) && "ring-2 ring-primary")}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <Checkbox checked={selectedProducts.includes(product.id)} onCheckedChange={() => toggleProductSelection(product.id)} onClick={(e) => e.stopPropagation()} />
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100"><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleOpenProductDialog(product)}><Pencil className="h-4 w-4 mr-2" />Modifica</DropdownMenuItem>
                        {selectedCatalog && <DropdownMenuItem onClick={() => handleRemoveFromCatalog(product.id, selectedCatalog)}><X className="h-4 w-4 mr-2" />Rimuovi</DropdownMenuItem>}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem className="text-destructive" onClick={() => { setDeletingItem({ type: 'product', id: product.id, name: product.name }); setDeleteDialogOpen(true); }}><Trash2 className="h-4 w-4 mr-2" />Elimina</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <div className="aspect-square bg-muted rounded-lg mb-3 flex items-center justify-center overflow-hidden">
                    {product.image_url ? <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" /> : <Package className="h-12 w-12 text-muted-foreground/50" />}
                  </div>
                  <h3 className="font-medium truncate">{product.name}</h3>
                  {product.sku && <p className="text-xs text-muted-foreground">SKU: {product.sku}</p>}
                  <div className="flex items-center justify-between mt-2">
                    <span className="font-bold text-primary">{formatCurrency(product.price)}/{product.unit}</span>
                    {!product.is_active && <Badge variant="secondary">Inattivo</Badge>}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="p-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12"><Checkbox checked={selectedProducts.length === filteredProducts.length && filteredProducts.length > 0} onCheckedChange={selectAllProducts} /></TableHead>
                  <TableHead>Prodotto</TableHead><TableHead>SKU</TableHead><TableHead>Categoria</TableHead>
                  <TableHead className="text-right">Prezzo</TableHead><TableHead>Stato</TableHead><TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredProducts.map(product => (
                  <TableRow key={product.id}>
                    <TableCell><Checkbox checked={selectedProducts.includes(product.id)} onCheckedChange={() => toggleProductSelection(product.id)} /></TableCell>
                    <TableCell><div className="flex items-center gap-3"><div className="w-10 h-10 bg-muted rounded flex items-center justify-center overflow-hidden">{product.image_url ? <img src={product.image_url} alt="" className="w-full h-full object-cover" /> : <Package className="h-5 w-5 text-muted-foreground/50" />}</div><span className="font-medium">{product.name}</span></div></TableCell>
                    <TableCell className="text-muted-foreground">{product.sku || '-'}</TableCell>
                    <TableCell>{product.category || '-'}</TableCell>
                    <TableCell className="text-right font-medium">{formatCurrency(product.price)}/{product.unit}</TableCell>
                    <TableCell><Badge variant={product.is_active ? 'default' : 'secondary'}>{product.is_active ? 'Attivo' : 'Inattivo'}</Badge></TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8"><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleOpenProductDialog(product)}><Pencil className="h-4 w-4 mr-2" />Modifica</DropdownMenuItem>
                          {selectedCatalog && <DropdownMenuItem onClick={() => handleRemoveFromCatalog(product.id, selectedCatalog)}><X className="h-4 w-4 mr-2" />Rimuovi</DropdownMenuItem>}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem className="text-destructive" onClick={() => { setDeletingItem({ type: 'product', id: product.id, name: product.name }); setDeleteDialogOpen(true); }}><Trash2 className="h-4 w-4 mr-2" />Elimina</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </ScrollArea>

      {/* Dialog Tutorial */}
      <Dialog open={showTutorial} onOpenChange={setShowTutorial}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lightbulb className="h-5 w-5 text-primary" />
              {tutorialStep === 0 && "Come funziona il Catalogo"}
              {tutorialStep === 1 && "Importa da Excel"}
              {tutorialStep === 2 && "Importa con AI"}
              {tutorialStep === 3 && "Organizza con i sotto-cataloghi"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {tutorialStep === 0 && (
              <div className="space-y-4">
                <div className="bg-muted/50 rounded-xl p-5 text-center">
                  <Package className="h-12 w-12 text-primary mx-auto mb-3" />
                  <p className="font-medium text-lg">Il tuo catalogo digitale</p>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Il catalogo è il cuore della piattaforma: qui inserisci i prodotti che vendi, con nome, prezzo e unità di misura. 
                </p>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  <strong>I tuoi clienti vedranno questo elenco</strong> quando vorranno fare un ordine, senza bisogno 
                  di chiamate, WhatsApp o fogli Excel. Tutto in un unico posto, sempre aggiornato.
                </p>
                <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg p-3">
                  <p className="text-sm text-green-800 dark:text-green-300">
                    <strong>Hai già un listino in Excel?</strong> Perfetto! Puoi importarlo in pochi secondi, senza riscrivere nulla.
                  </p>
                </div>
              </div>
            )}
            
            {tutorialStep === 1 && (
              <div className="space-y-4">
                <div className="bg-muted/50 rounded-xl p-5 text-center">
                  <FileSpreadsheet className="h-12 w-12 text-primary mx-auto mb-3" />
                  <p className="font-medium text-lg">Importa da file Excel o CSV</p>
                </div>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="bg-primary text-primary-foreground rounded-full w-7 h-7 flex items-center justify-center text-sm font-bold shrink-0">1</div>
                    <div>
                      <p className="font-medium text-sm">Carica il file</p>
                      <p className="text-sm text-muted-foreground">Seleziona il tuo file .xlsx o .csv dal computer</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="bg-primary text-primary-foreground rounded-full w-7 h-7 flex items-center justify-center text-sm font-bold shrink-0">2</div>
                    <div>
                      <p className="font-medium text-sm">Associa le colonne</p>
                      <p className="text-sm text-muted-foreground">Indica quale colonna corrisponde a nome, prezzo, unità, ecc.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="bg-primary text-primary-foreground rounded-full w-7 h-7 flex items-center justify-center text-sm font-bold shrink-0">3</div>
                    <div>
                      <p className="font-medium text-sm">Verifica e conferma</p>
                      <p className="text-sm text-muted-foreground">Controlla l'anteprima e clicca "Importa" — fatto!</p>
                    </div>
                  </div>
                </div>
                <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                  <p className="text-sm text-blue-800 dark:text-blue-300">
                    <strong>Non serve un formato specifico.</strong> Funziona con qualsiasi file Excel che abbia almeno il nome dei prodotti.
                  </p>
                </div>
              </div>
            )}
            
            {tutorialStep === 2 && (
              <div className="space-y-4">
                <div className="bg-muted/50 rounded-xl p-5 text-center">
                  <Sparkles className="h-12 w-12 text-primary mx-auto mb-3" />
                  <p className="font-medium text-lg">Importa con l'Intelligenza Artificiale</p>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Hai un listino in formato PDF, un'immagine, o un testo copiato da un'email? 
                  L'AI legge qualsiasi formato e <strong>crea automaticamente i prodotti</strong> con nome, prezzo e categoria.
                </p>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Basta incollare il testo o caricare il file: l'AI fa tutto il lavoro pesante. 
                  Tu devi solo verificare e confermare.
                </p>
                <div className="bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-800 rounded-lg p-3">
                  <p className="text-sm text-purple-800 dark:text-purple-300">
                    <strong>Ideale per listini non strutturati</strong> — anche da foto del foglio cartaceo!
                  </p>
                </div>
              </div>
            )}
            
            {tutorialStep === 3 && (
              <div className="space-y-4">
                <div className="bg-muted/50 rounded-xl p-5 text-center">
                  <FolderOpen className="h-12 w-12 text-primary mx-auto mb-3" />
                  <p className="font-medium text-lg">Organizza con i sotto-cataloghi</p>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Se hai tanti prodotti, puoi organizzarli in <strong>sotto-cataloghi</strong>. 
                  Pensa a delle "cartelle": puoi avere <em>Ortofrutta</em>, <em>Latticini</em>, <em>Bevande</em>, ecc.
                </p>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  I sotto-cataloghi ti aiutano a <strong>trovare velocemente</strong> un prodotto e a 
                  mostrare ai clienti un catalogo ordinato e professionale.
                </p>
                <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                  <p className="text-sm text-amber-800 dark:text-amber-300">
                    <strong>Non è obbligatorio!</strong> Puoi usare solo il catalogo generale. 
                    I sotto-cataloghi sono utili quando hai più di 30-40 prodotti.
                  </p>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter className="flex items-center justify-between sm:justify-between">
            <div className="flex items-center gap-1.5">
              {[0, 1, 2, 3].map(i => (
                <button
                  key={i}
                  onClick={() => setTutorialStep(i)}
                  className={cn(
                    "w-2 h-2 rounded-full transition-all",
                    tutorialStep === i ? "bg-primary w-6" : "bg-muted-foreground/30 hover:bg-muted-foreground/50"
                  )}
                />
              ))}
            </div>
            <div className="flex items-center gap-2">
              {tutorialStep > 0 && (
                <Button variant="ghost" size="sm" onClick={() => setTutorialStep(tutorialStep - 1)}>
                  Indietro
                </Button>
              )}
              {tutorialStep < 3 ? (
                <Button size="sm" onClick={() => setTutorialStep(tutorialStep + 1)}>
                  Avanti
                  <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              ) : (
                <Button size="sm" onClick={() => { setShowTutorial(false); dismissIntroBanner(); }}>
                  Ho capito, iniziamo!
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Prodotto */}
      <Dialog open={productDialogOpen} onOpenChange={setProductDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingProduct ? 'Modifica Prodotto' : 'Nuovo Prodotto'}</DialogTitle>
            <DialogDescription>
              {editingProduct ? 'Modifica i dettagli del prodotto' : 'Inserisci i dettagli del nuovo prodotto'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Nome *</Label>
              <Input
                id="name"
                value={productForm.name}
                onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                placeholder="Es. Pasta Barilla 500g"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="price">Prezzo *</Label>
                <Input
                  id="price"
                  type="number"
                  step="0.01"
                  value={productForm.price}
                  onChange={(e) => setProductForm({ ...productForm, price: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label htmlFor="unit">Unità</Label>
                <Select value={productForm.unit} onValueChange={(v) => setProductForm({ ...productForm, unit: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pz">Pezzo (pz)</SelectItem>
                    <SelectItem value="kg">Chilogrammo (kg)</SelectItem>
                    <SelectItem value="lt">Litro (lt)</SelectItem>
                    <SelectItem value="cf">Confezione (cf)</SelectItem>
                    <SelectItem value="ct">Cartone (ct)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="sku">SKU / Codice</Label>
                <Input
                  id="sku"
                  value={productForm.sku}
                  onChange={(e) => setProductForm({ ...productForm, sku: e.target.value })}
                  placeholder="ABC123"
                />
              </div>
              <div>
                <Label htmlFor="category">Categoria</Label>
                <Input
                  id="category"
                  value={productForm.category}
                  onChange={(e) => setProductForm({ ...productForm, category: e.target.value })}
                  placeholder="Es. Pasta"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="description">Descrizione</Label>
              <Textarea
                id="description"
                value={productForm.description}
                onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                placeholder="Descrizione del prodotto..."
                rows={3}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <Label htmlFor="is_active">Prodotto attivo</Label>
              <Switch
                id="is_active"
                checked={productForm.is_active}
                onCheckedChange={(checked) => setProductForm({ ...productForm, is_active: checked })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setProductDialogOpen(false)}>Annulla</Button>
            <Button onClick={handleSaveProduct} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingProduct ? 'Salva' : 'Crea'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Catalogo */}
      <Dialog open={catalogDialogOpen} onOpenChange={setCatalogDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCatalog ? 'Modifica Catalogo' : 'Nuovo Catalogo'}</DialogTitle>
            <DialogDescription>
              I cataloghi ti permettono di organizzare i prodotti in gruppi
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="catalog_name">Nome *</Label>
              <Input
                id="catalog_name"
                value={catalogForm.name}
                onChange={(e) => setCatalogForm({ ...catalogForm, name: e.target.value })}
                placeholder="Es. Catalogo Ristoranti"
              />
            </div>
            
            <div>
              <Label htmlFor="catalog_description">Descrizione</Label>
              <Textarea
                id="catalog_description"
                value={catalogForm.description}
                onChange={(e) => setCatalogForm({ ...catalogForm, description: e.target.value })}
                placeholder="Descrizione del catalogo..."
                rows={2}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="is_default">Catalogo predefinito</Label>
                <p className="text-xs text-muted-foreground">Assegnato automaticamente ai nuovi clienti</p>
              </div>
              <Switch
                id="is_default"
                checked={catalogForm.is_default}
                onCheckedChange={(checked) => setCatalogForm({ ...catalogForm, is_default: checked })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCatalogDialogOpen(false)}>Annulla</Button>
            <Button onClick={handleSaveCatalog} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingCatalog ? 'Salva' : 'Crea'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Aggiungi a Catalogo */}
      <Dialog open={addToCatalogDialogOpen} onOpenChange={setAddToCatalogDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Aggiungi a catalogo</DialogTitle>
            <DialogDescription>
              Seleziona il catalogo a cui aggiungere {selectedProducts.length} prodotti
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-2">
            {catalogs.map(catalog => (
              <Button
                key={catalog.id}
                variant="outline"
                className="w-full justify-start"
                onClick={() => handleAddToCatalog(catalog.id)}
              >
                <FolderOpen className="h-4 w-4 mr-2" />
                {catalog.name}
                <Badge variant="secondary" className="ml-auto">{catalog.product_count}</Badge>
              </Button>
            ))}
            
            {catalogs.length === 0 && (
              <p className="text-center text-muted-foreground py-4">
                Nessun catalogo disponibile. Creane uno prima.
              </p>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddToCatalogDialogOpen(false)}>Annulla</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Conferma Eliminazione */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Conferma eliminazione</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler eliminare {deletingItem?.type === 'product' ? 'il prodotto' : 'il catalogo'} "{deletingItem?.name}"? 
              Questa azione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ProductsPage;
